import { parseHtml } from "@use/parse-html";

import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";

import { ContentModelData, ElementModel, UnauthorizedElementModel } from "@courses-pm2/api/src/ts/models";
import { Engine } from "@courses-pm2/api/src/ts/utils";

import { parseElement } from "@courses-pm2/api/src/ts/parser/parseElement";
import { parsePlaceholder } from "./elements/parsePlaceholder";

/**
 * Helper that'll parse a content feed from the pm2 remote backend into a
 * ContentModel instance.
 */
export async function parseXml({ portalId, user, xml, engine }: {
    portalId: string;
    user?: AuthenticatedUserModel;
    xml: string;
    engine?: Engine;
}): Promise<ContentModelData> {
    // Parse the html into a DOM a dom
    const dom = parseHtml(xml, "text/xml");
    const promises: Promise<ElementModel | undefined>[] = [];

    for (const child of Array.from(dom.childNodes.item(0).childNodes)) {
        // Ignore anything but elements...
        if (!isElement(child)) {
            continue;
        }
        const accessCode = parseInt(child.getAttribute("access") || "0", 10);

        if (accessCode <= 2) {
            // if the content element hasn't been explicitly disabled for the
            // user then parse it's content
            promises.push(parseElement({
                node: child,
                portalId,
                user,
                engine,
            }));
        } else {
            // ... otherwise hide it completely by adding a placeholder in it's
            // place
            promises.push(Promise.resolve(parsePlaceholder({
                node: child,
                portalId,
                engine,
            })));
        }
    }

    // Wait for all elements to be properly parsed
    const elements = <ElementModel[]>(await Promise.all(promises)).filter((element) => element !== undefined);

    // Next up, determine if the user has actually been authorized access to
    // this piece of content (ie. if there are any elements that isn't instances
    // of UnauthorizedElementModels)
    const isAuthorized = !elements.find((element) => element instanceof UnauthorizedElementModel);
    const isScrambled = !!elements.find((element) => element.isScrambled);

    // Now return the parsed model
    return { elements, isAuthorized, isScrambled };
}

/**
 * Typeguard that verifies that the given child is an actual element and not a
 * pure text representation.
 */
function isElement(element: Document | Node): element is Element {
    return element.nodeType === element.ELEMENT_NODE;
}
